import type { Hit } from 'algoliasearch';
import { isParsableDate } from 'dotcom-core/src/helpers/date';
import type { ProductInterface } from '~/graphql';
import type {
  BasePrice,
  BaseSavedPrice,
  Maybe,
  ProductDetailPageQuery,
  ProductVariant,
} from '~/graphql';
import { isTruthy } from '~/helpers/predicate';
import type { ObjectKeyPaths } from './types';

export type PdpProductInterface = NonNullable<
  NonNullable<ProductDetailPageQuery['productDetailPage']>['product']
>;

export function getVariant(product: PdpProductInterface) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';

  const loyaltyVariant =
    isConfigurableProduct && product.loyaltyVariant
      ? product.loyaltyVariant
      : null;

  const defaultVariant = isConfigurableProduct ? product.defaultVariant : null;

  return loyaltyVariant || defaultVariant || null;
}

export function getLoyaltyVariant(product: PdpProductInterface) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';

  const loyaltyVariant =
    isConfigurableProduct && product.loyaltyVariant
      ? product.loyaltyVariant
      : null;

  return loyaltyVariant;
}

type Props = { variant?: ProductVariant | null; product: PdpProductInterface };

export function getDiscountPrices({ variant, product }: Props) {
  const bundleProduct = product.__typename === 'BundleProduct';
  const configurableProduct = product.__typename === 'ConfigurableProduct';
  const isLoyaltyVariant = isTruthy(getLoyaltyVariant(product));

  let previousPrice: Maybe<BasePrice> | undefined;
  let savingsPrice: Maybe<BaseSavedPrice> | undefined;
  let previousPriceMessage:
    | 'regularPrice'
    | 'fullVersionPrice'
    | 'totalPriceSeparately'
    | 'regularUpdatePrice'
    | undefined;

  const isVariantOnSale = isTruthy(variant?.onSale);
  const isProductOnSale = isTruthy(product?.onSale);

  if (
    (isVariantOnSale && !isLoyaltyVariant) ||
    (bundleProduct && isProductOnSale)
  ) {
    previousPrice = variant?.price?.regular;
    savingsPrice = variant?.price?.savings?.regularToFinal;
    previousPriceMessage = 'regularPrice';
  }

  if (configurableProduct && isVariantOnSale && isLoyaltyVariant) {
    previousPrice = variant?.price?.regular;
    savingsPrice = variant?.price?.savings?.regularToFinal;
    previousPriceMessage = 'regularUpdatePrice';
  }

  if (configurableProduct && isLoyaltyVariant && !isVariantOnSale) {
    previousPrice = product?.price?.regular;
    savingsPrice = product?.price?.savings?.regularToLoyalty;
    previousPriceMessage = 'fullVersionPrice';
  }

  if (
    (configurableProduct && !isLoyaltyVariant && !isVariantOnSale) ||
    (bundleProduct && !isProductOnSale)
  ) {
    previousPrice = product?.price?.totalBundle;
    savingsPrice = product?.price?.savings?.totalBundleToFinal;
    previousPriceMessage = 'totalPriceSeparately';
  }

  return { previousPrice, savingsPrice, previousPriceMessage };
}

export function getReleaseDate({
  product,
  variant,
}: {
  product: PdpProductInterface;
  variant: ProductVariant | undefined | null;
}): Date | null {
  if (variant && isParsableDate(variant.releaseDate)) {
    return new Date(variant.releaseDate);
  }

  if (!variant && isParsableDate(product.releaseDate)) {
    return new Date(product.releaseDate);
  }

  return null;
}

export function getStockStatus({ product, variant }: Props): string {
  // We only consider stockStatus property from variants and BundleProducts.
  const isBundleProduct = product.__typename === 'BundleProduct';

  if (!variant && isBundleProduct) {
    return product.stockStatus;
  }

  if (variant) {
    return variant.stockStatus;
  }

  return 'IN_STOCK';
}

export function getIsShippable({ product, variant }: Props): boolean {
  // Only variants are shippable together with products with BundleProduct type.
  // SimpleProducts/VirtualProducts are part of ConfigurableProducts, so we
  // handle them through variants (defaultVariant).
  const isProductTypeShippable = product.__typename === 'BundleProduct';

  if (!variant && isProductTypeShippable) {
    return product.shippable;
  }

  if (variant) {
    return variant?.shippable;
  }

  return false;
}
export function getLinkedProductsForSelector(
  product: ProductInterface,
  linkedProductDetailPages?: {
    slug: string;
    product: ProductInterface;
  }[],
) {
  if (!linkedProductDetailPages?.length) {
    return [];
  }
  return [
    {
      slug: product.slug,
      name: product.name,
      description: product.description,
      shortDescription: product.shortDescription,
      currency: product.price?.final.currency,
      value: product.price?.final.value || 0,
    },
    ...(linkedProductDetailPages ?? []).map(({ slug, product }) => ({
      slug,
      name: product.name,
      description: product.description,
      shortDescription: product.shortDescription,
      currency: product.price?.final.currency,
      value: product.price?.final.value || 0,
    })),
  ].sort((a, b) => {
    const valueComparison = (a.value ?? 0) - (b.value ?? 0); // if value is same, sort by name
    return valueComparison === 0
      ? (a.name ?? '').localeCompare(b.name ?? '')
      : valueComparison;
  });
}

export type AlgoliaProductHit = Hit<{
  categories: string[];
  description: string;
  featured_image: string;
  is_free: boolean;
  hierarchy: {
    lvl0: string[];
    lvl1: string[];
    lvl2: string[];
  };
  slug_hierarchy: {
    lvl0: string[];
    lvl1: string[];
    lvl2: string[];
  };
  ibexa_product_id: number;
  audios: AlgoliaAudio[];
  featured_image_variants: {
    alt_text: string;
    file_name: string;
    ibexa_content_id: number;
    id: string;
    src_set: {
      mime_type: string;
      url: string;
      width: number;
      pixel_ratio: string;
    }[];
    url: string;
  };
  llm_keywords: string[];
  magento_product_id: number;
  master_sku: string;
  num_variants: number;
  product_name: string;
  product_variants: {
    entity_id: number;
    license_type: string;
    name: string;
    serial_system: string;
    serial_system_reference: string;
    sku: string;
  }[];
  sort_release_date: Date;
  item_type: 'Software' | 'Hardware' | 'Accessories';
  product_slug: string;
  web_tour_page: string;
  pdp_slug: string;
  pdp_url: string;
  objectID: string;
  prices?: Record<string, AlgoliaProductPrice>;
}>;

export type AlgoliaAudio = {
  audio_id: number;
  audio_name: string;
  filename: string;
  mime_type: string;
  description: string;
  title: string;
  uri: string;
  original_filename: string;
};

export type AlgoliaProductHitAttribute = ObjectKeyPaths<AlgoliaProductHit>;

type AlgoliaProductPrice = {
  full_version_final_price: number;
  full_version_license_type: string;
  full_version_price: number;
  full_version_sku: string;

  smallest_variant_final_price?: number;
  smallest_variant_license_type?: string;
  smallest_variant_price?: number;
  smallest_variant_sku?: number;
};
